import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { ModalError } from '@vabembarqueja/embarque-ja-shared-components';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Recibo } from 'src/models/Recibo';
import { Routes } from 'src/routes/index.routes';
import { CarteiraService } from 'src/services';

import Logo from '../../assets/AB_EMBARQUEJA_LOGO.png';
import LogoFooter from '../../assets/logo-aguia-branca.png';
import { Colors } from '../../shared/colors';
import CpfCnpjFormatter from '../components/CpfCnpjFormatter';

const ReciboPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [recibo, setRecibo] = useState<Recibo | null>(null);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { id } = useParams<{ id: string }>();

  const irParaHome = () => navigate(Routes.Home);
  const handleCloseModalError = () => {
    setError(false);
  };
  useEffect(() => {
    setLoading(true);
    if (id) {
      CarteiraService.getReciboById(id)
        .then(setRecibo)
        .catch((err: Error) => {
          setError(true);
          setErrorMessage(err.message);
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading) {
    return (
      <Box height="100vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <>
      <style>
        {`
          @media print {
            body {
              margin: 0;
              -webkit-print-color-adjust: exact;
            }

            @page {
              size: A4;
              margin: 20mm;
            }

            header, .app-bar, .header, .no-print {
              display: none !important;
            }
          }
        `}
      </style>

      <Box
        sx={{
          width: '210mm',
          minHeight: '297mm',
          p: '20mm',
          mx: 'auto',
          bgcolor: theme.palette.common.white,
          boxShadow: '0 0 5px rgba(0,0,0,0.1)',
          fontFamily: 'Arial, sans-serif'
        }}
      >
        {/* Logo topo */}
        <Box display="flex" justifyContent="center">
          <img src={Logo} alt="Logo" height={100} />
        </Box>

        {/* Título */}
        <Box textAlign="center" mt={4} mb={5}>
          <Typography
            variant="h5"
            fontWeight={400}
            position="relative"
            display="inline-block"
            sx={{
              display: 'inline-block',
              borderBottom: '1px solid black',
              pb: 0.5
            }}
          >
            RECIBO DE VENDA – EMBARQUE JÁ
          </Typography>
        </Box>

        {/* Dados do cliente */}
        <Box mb={4}>
          <Box display="flex" mb={0.5} gap={1}>
            <Typography fontWeight="bold">Cadastro:</Typography>
            <Typography>{recibo?.razaoSocial || recibo?.nome}</Typography>
          </Box>

          <Box display="flex" mb={0.5} gap={1}>
            <Typography fontWeight="bold">Nome:</Typography>
            <Typography>{recibo?.nome}</Typography>
          </Box>

          <Box display="flex" mb={0.5} gap={1}>
            <Typography fontWeight="bold">
              {recibo?.cpfCnpj && recibo.cpfCnpj.length > 11 ? 'CNPJ:' : 'CPF:'}
            </Typography>
            <Typography>
              <CpfCnpjFormatter value={recibo?.cpfCnpj || ''} />
            </Typography>
          </Box>

          <Box display="flex" mb={0.5} gap={1}>
            <Typography fontWeight="bold">Endereço:</Typography>
            <Typography>{recibo?.endereco}</Typography>
          </Box>

          <Box display="flex" mb={0.5} gap={1}>
            <Typography fontWeight="bold">Telefone:</Typography>
            <Typography>{recibo?.telefone}</Typography>
          </Box>
        </Box>

        {/* Detalhes da venda */}
        <Box bgcolor={Colors.gray2} p={3} borderRadius={6}>
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h6"
              fontWeight={400}
              position="relative"
              display="inline-block"
              sx={{
                display: 'inline-block',
                borderBottom: '1px solid black',
                pb: 0.5
              }}
            >
              DETALHES DA VENDA
            </Typography>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box flex={1} minWidth={250}>
              <Box mb={2}>
                <Typography>
                  <strong>Nº do Recibo/Autorização:</strong>
                </Typography>
                <Typography>{recibo?.numeroRecibo}</Typography>
              </Box>
              <Box mb={2}>
                <Typography>
                  <strong>Período de Referência:</strong>
                </Typography>
                <Typography>{recibo?.dataTransacaoFormatada}</Typography>
              </Box>
              <Box mb={2}>
                <Typography>
                  <strong>Valor da Carga:</strong>
                </Typography>
                <Typography>{recibo?.valorCargaFormatado}</Typography>
              </Box>
            </Box>

            <Box flex={1} minWidth={250}>
              <Box mb={2}>
                <Typography>
                  <strong>Beneficiário Destinatário:</strong>
                </Typography>
                <Typography>{recibo?.nome}</Typography>
              </Box>
              <Box mb={2}>
                <Typography>
                  <strong>Pagamento via:</strong>
                </Typography>
                <Typography>{recibo?.origem}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Rodapé legal */}
        <Typography mt={4}>
          Este documento está embasado na legislação vigente, em especial o <br />
          DECRETO Nº 10.854, DE 10 DE NOVEMBRO DE 2021, artigos 126 e 128.
        </Typography>

        {/* Emitente */}
        <Box mt={6} textAlign="left" color={theme.palette.grey[700]}>
          <Typography>
            <strong>Emitente (Empresa Vendedora):</strong>
          </Typography>
          <img
            src={LogoFooter}
            alt="Águia Branca"
            style={{ height: 15, marginBottom: 8, marginTop: 15 }}
          />
          <Typography>
            Viação Águia Branca S.A. | Av. Mario Gurgel, 5030 | Cariacica - ES.
            <br />
            CEP: 29145-901 | CNPJ: 27.486.182/0001-09 | sac@aguiabranca.com.br
          </Typography>
        </Box>

        {/* Botões de ação */}
        <Box className="no-print" mt={5} display="flex" gap={2}>
          <Button
            onClick={irParaHome}
            variant="contained"
            sx={{ bgcolor: theme.palette.grey[700], color: 'white' }}
          >
            Voltar
          </Button>
          <Button onClick={() => window.print()} variant="contained" color="primary">
            Imprimir Recibo
          </Button>
        </Box>
        <ModalError
          open={error}
          setOpen={setError}
          message={errorMessage}
          onClose={handleCloseModalError}
        />
      </Box>
    </>
  );
};

export default ReciboPage;
